import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const BAR_HEIGHTS = [55, 90, 40, 75, 60];
const BAR_DURATIONS = ['0.9s', '0.7s', '1.1s', '0.8s', '1.0s'];
const BAR_DELAYS = ['0s', '0.15s', '0.3s', '0.1s', '0.25s'];

const SECONDS_PER_ARTIST = 1.5;
const MIN_ESTIMATED_MS = 4000;
const MAX_ESTIMATED_MS = 120000;
const MIN_DISPLAY_MS = 4000;

// Shown by OAuthHandler outside every <Suspense>, so it reads `common` only.
const UserLoading = ({ artistCount, isReady, onDone, logoAnimated = false }) => {
  const { t } = useTranslation('common');
  const [progress, setProgress] = useState(0);
  const [isCompleting, setIsCompleting] = useState(false);
  const mountedAt = useRef(Date.now());
  const completionScheduled = useRef(false);
  const logoRef = useRef(null);

  const estimatedMs = Math.min(
    MAX_ESTIMATED_MS,
    Math.max(MIN_ESTIMATED_MS, artistCount * SECONDS_PER_ARTIST * 1000)
  );

  // Animate bar to 70% (not 90%) so there's visible space before the completion snap,
  // and use linear timing to avoid an ease-out flat spot near the target.
  useEffect(() => {
    const t = setTimeout(() => setProgress(70), 150);
    return () => clearTimeout(t);
  }, []);

  const triggerCompletion = useCallback(() => {
    if (completionScheduled.current) return;
    completionScheduled.current = true;

    const elapsed = Date.now() - mountedAt.current;
    const remaining = Math.max(0, MIN_DISPLAY_MS - elapsed);

    setTimeout(() => {
      setIsCompleting(true);
      setProgress(100);
      setTimeout(() => {
        if (logoRef.current) {
          const r = logoRef.current.getBoundingClientRect();
          const rectJson = JSON.stringify({
            left: r.left, top: r.top, width: r.width, height: r.height,
          });
          sessionStorage.setItem('loading_logo_rect', rectJson);
          // Hint for the onboarding submit animation: actual measured position so
          // the next run doesn't have to estimate from CSS constants.
          sessionStorage.setItem('loading_logo_hint_rect', rectJson);
          sessionStorage.setItem('recs_entering', '1');
        }
        onDone();
      }, 550);
    }, remaining);
  }, [onDone]);

  // React to the parent signalling that enrichment is complete
  useEffect(() => {
    if (isReady) triggerCompletion();
  }, [isReady, triggerCompletion]);

  const progressTransition =
    progress === 0
      ? 'none'
      : isCompleting
        ? 'width 0.5s ease-in-out'
        : `width ${estimatedMs / 1000}s linear`;

  return (
    <div className="fixed inset-0 bg-white flex flex-col items-center justify-center z-[300] px-6">
      <img
        ref={logoRef}
        src="/assets/images/applauss_18__x2-1758554505305.png"
        alt="Applauss"
        className={`h-10 mb-14 object-contain${logoAnimated ? ' opacity-0 animate-fade-in' : ''}`}
      />

      <div className="flex items-end gap-1.5 mb-10" style={{ height: '3rem' }}>
        {BAR_HEIGHTS.map((h, i) => (
          <div
            key={i}
            className="w-2 bg-black rounded-full animate-bounce"
            style={{
              height: `${h}%`,
              animationDuration: BAR_DURATIONS[i],
              animationDelay: BAR_DELAYS[i],
            }}
          />
        ))}
      </div>

      <div className="text-center mb-8 space-y-1.5">
        <p className="text-base font-semibold text-black">
          {t('userLoadingTitle')}
        </p>
        <p className="text-sm text-black/70">
          {t('userLoadingSubtitle')}
        </p>
      </div>

      <div className="w-64 rounded-full overflow-hidden bg-black/15" style={{ height: '6px' }}>
        <div
          className="h-full bg-black rounded-full"
          style={{ width: `${progress}%`, transition: progressTransition }}
        />
      </div>
      <p className="mt-3 text-xs text-black/60">{t('userLoadingWait')}</p>
    </div>
  );
};

export default UserLoading;
